import {
  axiosStorableError,
  storableError,
} from '../../util/errors';
import {
  currentUserShowSuccess,
  setCurrentUser,
} from '../../ducks/user.duck';
import {
  handleAxiosResponse,
} from '../../util/apiHelpers';
import {
  restApiService,
} from '../../services/services';

const IS_OPEN_SET = 'app/AddPhoneModal/IS_OPEN_SET';

const SET_CHANNEL = 'app/AddPhoneModal/SET_CHANNEL';

const ADD_PHONE_REQUEST = 'app/AddPhoneModal/ADD_PHONE_REQUEST';
const ADD_PHONE_SUCCESS = 'app/AddPhoneModal/ADD_PHONE_SUCCESS';
const ADD_PHONE_ERROR = 'app/AddPhoneModal/ADD_PHONE_ERROR';

const SEND_CODE_REQUEST = 'app/AddPhoneModal/SEND_CODE_REQUEST';
const SEND_CODE_SUCCESS = 'app/AddPhoneModal/SEND_CODE_SUCCESS';
const SEND_CODE_ERROR = 'app/AddPhoneModal/SEND_CODE_ERROR';

const VERIFY_CODE_REQUEST = 'app/AddPhoneModal/VERIFY_CODE_REQUEST';
const VERIFY_CODE_SUCCESS = 'app/AddPhoneModal/VERIFY_CODE_SUCCESS';
const VERIFY_CODE_ERROR = 'app/AddPhoneModal/VERIFY_CODE_ERROR';

const RESET_STATE = 'app/AddPhoneModal/RESET_STATE';

// ================ Reducer ================ //

const initialState = {
  isOpen: false,
  isRequired: false,
  addPhoneInProgress: false,
  addPhoneError: null,
  addedPhone: null,
  sendCodeInProgress: false,
  isSendCodeSuccess: false,
  sendCodeError: null,
  channel: 'sms',
  verifyCodeInProgress: false,
  verifyCodeError: null,
  verifiedPhoneForBrokerSignup: null,
};

const addPhoneModalReducer = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case IS_OPEN_SET:
      return {
        ...(payload.isOpen ? initialState : state),
        isOpen: payload.isOpen,
        addedPhone: payload.addedPhone,
        isRequired: payload.isRequired,
      };

    case SET_CHANNEL:
      return {
        ...state,
        channel: payload,
      };

    case ADD_PHONE_REQUEST:
      return {
        ...state,
        addPhoneInProgress: true,
        addPhoneError: null,
        addedPhone: null,
      };

    case ADD_PHONE_SUCCESS:
      return {
        ...state,
        addPhoneInProgress: false,
        addedPhone: payload,
      };

    case ADD_PHONE_ERROR:
      return {
        ...state,
        addPhoneInProgress: false,
        addPhoneError: payload,
      };

    case SEND_CODE_REQUEST:
      return {
        ...state,
        sendCodeInProgress: true,
        sendCodeError: null,
        isSendCodeSuccess: false,
      };

    case SEND_CODE_SUCCESS:
      return {
        ...state,
        sendCodeInProgress: false,
        isSendCodeSuccess: true,
      };

    case SEND_CODE_ERROR:
      return {
        ...state,
        sendCodeInProgress: false,
        sendCodeError: payload,
      };

    case VERIFY_CODE_REQUEST:
      return {
        ...state,
        verifyCodeInProgress: true,
        verifyCodeError: null,
      };

    case VERIFY_CODE_SUCCESS:
      return {
        ...state,
        verifyCodeInProgress: false,
        verifiedPhoneForBrokerSignup: payload,
      };

    case VERIFY_CODE_ERROR:
      return {
        ...state,
        verifyCodeInProgress: false,
        verifyCodeError: payload,
      };

    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default addPhoneModalReducer;

// ================ Action creators ================ //

const isOpenSet = (value, verifyingNumber, isRequired) => ({
  type: IS_OPEN_SET,
  payload: {
    isOpen: value,
    addedPhone: verifyingNumber,
    isRequired,
  }
});

const setChannelAction = value => ({
  type: SET_CHANNEL,
  payload: value,
});

const addPhoneRequest = () => {
  return {
    type: ADD_PHONE_REQUEST,
  };
};

const addPhoneSuccess = addedPhone => {
  return {
    type: ADD_PHONE_SUCCESS,
    payload: addedPhone,
  };
};

const addPhoneError = error => {
  return {
    type: ADD_PHONE_ERROR,
    payload: error,
  };
};

const sendCodeRequest = () => {
  return {
    type: SEND_CODE_REQUEST,
  };
};

const sendCodeSuccess = () => {
  return {
    type: SEND_CODE_SUCCESS,
  };
};

const sendCodeError = error => {
  return {
    type: SEND_CODE_ERROR,
    payload: error,
  };
};

const verifyCodeRequest = () => {
  return {
    type: VERIFY_CODE_REQUEST,
  };
};

const verifyCodeSuccess = verifiedPhone => {
  return {
    type: VERIFY_CODE_SUCCESS,
    payload: verifiedPhone,
  };
};

const verifyCodeError = error => {
  return {
    type: VERIFY_CODE_ERROR,
    payload: error,
  };
};

const resetStateAction = () => {
  return {
    type: RESET_STATE,
  };
};

// ================ Selectors ================ //

export const $modalState = state => {
  return state.AddPhoneModal;
};

export const $addedPhone = state => {
  const subState = $modalState(state);
  return subState.addedPhone;
};

export const $verifiedPhoneForBrokerSignup = state => {
  const subState = $modalState(state);
  return subState.verifiedPhoneForBrokerSignup;
};

// ================ Thunk ================ //

export const openModal = (verifyingPhone = null, isRequired = false) => dispatch => {
  dispatch(isOpenSet(true, verifyingPhone, isRequired));
};

export const closeModal = () => dispatch => {
  dispatch(isOpenSet(false));
};

export const resetState = () => dispatch => {
  dispatch(resetStateAction());
};

export const setChannel = val => dispatch => {
  dispatch(setChannelAction(val));
}

export const addPhone = phone => dispatch => {
  if (!phone) {
    return Promise.resolve();
  }
  dispatch(addPhoneRequest());
  return restApiService.secureInstance
    .postRaw('user/add-phone', phone)
    .then(handleAxiosResponse)
    .then(resp => {
      dispatch(setCurrentUser(resp));
      dispatch(addPhoneSuccess(phone));
      return resp;
    })
    .catch(e => {
      dispatch(addPhoneError(storableError(e)));
      throw e;
    })
  ;
};

export const sendCode = (phone, channel) => dispatch => {
  dispatch(sendCodeRequest());
  return restApiService.secureInstance
    .postRaw('verify-phone/send-verification-code', {
      to: `+${phone}`,
      channel,
    })
    .then(resp => {
      dispatch(sendCodeSuccess());
      return resp;
    })
    .catch(e => {
      dispatch(sendCodeError(storableError(e)));
      throw e;
    })
  ;
};

export const sendCodeForBrokerSignup = (phone, channel, token) => dispatch => {
  dispatch(sendCodeRequest());
  return restApiService.secureInstance
    .postRaw('brokers/send-verification-code-for-broker-signup', {
      to: `+${phone}`,
      channel,
      token,
    })
    .then(resp => {
      dispatch(sendCodeSuccess());
      return resp;
    })
    .catch(e => {
      dispatch(sendCodeError(axiosStorableError(e)));
      throw e;
    })
  ;
};

export const verifyCodeForBrokerSignup = (phone, code, token, onBeforeClose) => (dispatch, getState) => {
  dispatch(verifyCodeRequest());
  return restApiService.secureInstance
    .postRaw('brokers/check-verification-code-for-broker-signup', {
      to: `+${phone}`,
      code,
      token,
    })
    .then(handleAxiosResponse)
    .then(() => {
      const state = getState();
      const addedPhone = $addedPhone(state);
      dispatch(verifyCodeSuccess(addedPhone));
      onBeforeClose($verifiedPhoneForBrokerSignup(getState()));
      dispatch(closeModal());
      return addedPhone;
    })
    .catch(e => {
      dispatch(verifyCodeError(axiosStorableError(e)));
      throw e;
    })
  ;
};

export const verifyCode = (phone, code) => dispatch => {
  dispatch(verifyCodeRequest());
  return restApiService.secureInstance
    .postRaw('verify-phone/check-verification-code', {
      to: `+${phone}`,
      code,
    })
    .then(handleAxiosResponse)
    .then(resp => {
      const user = resp.data.data;
      dispatch(currentUserShowSuccess(user));
      dispatch(verifyCodeSuccess());
      dispatch(closeModal());
      return resp;
    })
    .catch(e => {
      dispatch(verifyCodeError(axiosStorableError(e)));
      throw e;
    })
  ;
}
